// WebGL2 fisheye dewarp: the raw disk is one texture, and every view is a
// virtual pinhole camera at the lens centre, drawn by one fragment shader.
// Nothing is pre-baked, so yaw / tilt / zoom are uniforms and a look-around
// costs a draw call.

import {
  DEG,
  MODEL_INDEX,
  aimAt,
  basis,
  circlePx,
  clampFov,
  clampTilt,
  dirToPx,
  lensFrame,
  moveWithinLens,
  pxToDir,
  settle,
  wrapYaw,
  type Circle,
  type CalibratedLens,
  type Pose,
  type Vec3,
} from "./lens";

export type Layout = "single" | "quad" | "raw";

type Rect = { x: number; y: number; w: number; h: number };
type Tile = { kind: "view"; idx: number; rect: Rect } | { kind: "raw"; rect: Rect; inset?: boolean };

const VS = `#version 300 es
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

const FS = `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform vec2 uTexSize;
uniform vec3 uCircle;
uniform float uThetaMax;
uniform int uModel;
uniform vec3 uAxis;
uniform vec3 uEx;
uniform vec3 uEy;
uniform int uMode;
uniform vec3 uF;
uniform vec3 uR;
uniform vec3 uU;
uniform float uTanHalf;
uniform vec4 uRect;
uniform vec3 uRawFit;
out vec4 outColor;
const vec3 BG = vec3(0.04, 0.04, 0.05);
float g(float th) {
  if (uModel == 1) return sin(th * 0.5);
  if (uModel == 2) return tan(th * 0.5);
  if (uModel == 3) return sin(min(th, 1.5707963));
  return th;
}
void main() {
  vec2 px;
  float inside;
  if (uMode == 1) {
    vec2 local = vec2(gl_FragCoord.x - uRect.x, uRect.y + uRect.w - gl_FragCoord.y);
    px = uRawFit.xy + local * uRawFit.z;
    inside = 1.0;
  } else {
    vec2 ndc = (gl_FragCoord.xy - uRect.xy) / uRect.zw * 2.0 - 1.0;
    float aspect = uRect.z / uRect.w;
    vec3 d = normalize(uF + ndc.x * uTanHalf * aspect * uR + ndc.y * uTanHalf * uU);
    float th = acos(clamp(dot(d, uAxis), -1.0, 1.0));
    float rho = uCircle.z * g(th) / g(uThetaMax);
    vec2 q = vec2(dot(d, uEx), dot(d, uEy));
    float h = length(q);
    vec2 dir = h > 1e-6 ? q / h : vec2(1.0, 0.0);
    px = uCircle.xy + rho * dir;
    inside = step(th, uThetaMax + 1e-4);
  }
  // Sample unconditionally so mip selection sees continuous derivatives.
  vec3 c = texture(uTex, px / uTexSize).rgb;
  inside *= step(0.0, px.x) * step(0.0, px.y) * step(px.x, uTexSize.x) * step(px.y, uTexSize.y);
  outColor = vec4(mix(BG, c, inside), 1.0);
}`;

const UNIFORMS = [
  "uTex", "uTexSize", "uCircle", "uThetaMax", "uModel", "uAxis", "uEx", "uEy",
  "uMode", "uF", "uR", "uU", "uTanHalf", "uRect", "uRawFit",
] as const;

export const VIEW_COLORS = ["#f59e0b", "#22d3ee", "#a3e635", "#f472b6"];

export class FisheyeRenderer {
  lens: CalibratedLens;
  views: Pose[];
  active = 0;
  layout: Layout = "single";
  /** Single layout: show the whole disk as a small inset with the view outline. */
  inset = true;
  interactive = true;
  onChange?: (views: Pose[], active: number) => void;
  /** After every draw: the view being looked at and its frame's aspect, or
   *  null when the whole fisheye is on screen. */
  onRender?: (focus: { view: Pose; aspect: number } | null) => void;

  private gl: WebGL2RenderingContext;
  private overlay: CanvasRenderingContext2D;
  private prog: WebGLProgram;
  private tex: WebGLTexture;
  private u: Record<(typeof UNIFORMS)[number], WebGLUniformLocation | null>;
  private texW = 0;
  private texH = 0;
  private drag: { tile: Tile; x: number; y: number; moved: boolean } | null = null;
  private suppressClick = false;
  private resizeObserver: ResizeObserver;
  private cleanup: Array<() => void> = [];

  constructor(
    private canvas: HTMLCanvasElement,
    private overlayCanvas: HTMLCanvasElement,
    lens: CalibratedLens,
    views: Pose[],
  ) {
    this.lens = lens;
    this.views = views.map((v) => ({ ...v }));
    const gl = canvas.getContext("webgl2", { antialias: false, preserveDrawingBuffer: false });
    const octx = overlayCanvas.getContext("2d");
    if (!gl || !octx) throw new Error("WebGL2 is not available in this browser");
    this.gl = gl;
    this.overlay = octx;
    this.prog = this.link();
    this.u = Object.fromEntries(UNIFORMS.map((n) => [n, gl.getUniformLocation(this.prog, n)])) as FisheyeRenderer["u"];
    gl.bindVertexArray(gl.createVertexArray());
    const tex = gl.createTexture();
    if (!tex) throw new Error("WebGL texture allocation failed");
    this.tex = tex;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.bindInput();
    this.resize();
  }

  get hasFrame() {
    return this.texW > 0;
  }

  get frameSize() {
    return { width: this.texW, height: this.texH };
  }

  /** Upload a frame (video element, VideoFrame, image) and redraw. */
  setFrame(src: TexImageSource, width: number, height: number) {
    if (!width || !height) return;
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
    gl.generateMipmap(gl.TEXTURE_2D);
    this.texW = width;
    this.texH = height;
    this.render();
  }

  setViews(views: Pose[], active = this.active) {
    this.views = views.map((v) => ({ ...v }));
    this.active = Math.min(active, Math.max(0, this.views.length - 1));
    this.render();
  }

  destroy() {
    this.resizeObserver.disconnect();
    for (const fn of this.cleanup) fn();
    const lose = this.gl.getExtension("WEBGL_lose_context");
    lose?.loseContext();
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
    for (const cv of [this.canvas, this.overlayCanvas]) {
      if (cv.width !== w || cv.height !== h) {
        cv.width = w;
        cv.height = h;
      }
    }
    this.render();
  }

  // ---------------------------------------------------------------- layout

  private tiles(): Tile[] {
    const W = this.canvas.width;
    const H = this.canvas.height;
    if (this.layout === "raw") return [{ kind: "raw", rect: { x: 0, y: 0, w: W, h: H } }];
    if (this.layout === "quad") {
      const size = Math.min(Math.round(W * 0.46), H);
      const out: Tile[] = [{ kind: "raw", rect: { x: 0, y: Math.round((H - size) / 2), w: size, h: size } }];
      const qw = Math.floor((W - size) / 2);
      const qh = Math.floor(H / 2);
      // 16:9 pictures, like the camera's own PTZ outputs, so a view's FOV
      // covers the same space whatever the window shape.
      const vw = Math.min(qw, Math.round((qh * 16) / 9));
      const vh = Math.round((vw * 9) / 16);
      for (let i = 0; i < Math.min(4, this.views.length); i++) {
        out.push({
          kind: "view",
          idx: i,
          rect: {
            x: size + (i % 2) * qw + Math.floor((qw - vw) / 2),
            y: Math.floor(i / 2) * qh + Math.floor((qh - vh) / 2),
            w: vw,
            h: vh,
          },
        });
      }
      return out;
    }
    const tiles: Tile[] = [{ kind: "view", idx: this.active, rect: { x: 0, y: 0, w: W, h: H } }];
    if (this.inset) {
      const m = Math.round(12 * (window.devicePixelRatio || 1));
      const size = Math.round(Math.min(W, H) * 0.3);
      tiles.push({ kind: "raw", rect: { x: m, y: H - size - m, w: size, h: size }, inset: true });
    }
    return tiles;
  }

  private circle(): Circle {
    return circlePx(this.lens, this.texW, this.texH);
  }

  private rawFit(rect: Rect) {
    const c = this.circle();
    const k = (2 * c.r * 1.04) / Math.min(rect.w, rect.h);
    return { ox: c.cx - (rect.w * k) / 2, oy: c.cy - (rect.h * k) / 2, k };
  }

  private aspectOf(idx: number) {
    const t = this.tiles().find((x) => x.kind === "view" && x.idx === idx);
    return t ? t.rect.w / t.rect.h : 16 / 9;
  }

  // ---------------------------------------------------------------- draw

  render() {
    const gl = this.gl;
    const W = this.canvas.width;
    const H = this.canvas.height;
    gl.disable(gl.SCISSOR_TEST);
    gl.viewport(0, 0, W, H);
    gl.clearColor(0.04, 0.04, 0.05, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    this.overlay.clearRect(0, 0, W, H);
    if (!this.hasFrame) return;

    const c = this.circle();
    const L = lensFrame(this.lens);
    gl.useProgram(this.prog);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.uniform1i(this.u.uTex, 0);
    gl.uniform2f(this.u.uTexSize, this.texW, this.texH);
    gl.uniform3f(this.u.uCircle, c.cx, c.cy, c.r);
    gl.uniform1f(this.u.uThetaMax, this.lens.theta_max_deg * DEG);
    gl.uniform1i(this.u.uModel, MODEL_INDEX[this.lens.model]);
    gl.uniform3f(this.u.uAxis, ...L.axis);
    gl.uniform3f(this.u.uEx, ...L.ex);
    gl.uniform3f(this.u.uEy, ...L.ey);
    gl.enable(gl.SCISSOR_TEST);

    const tiles = this.tiles();
    for (const t of tiles) {
      const { x, y, w, h } = t.rect;
      const glY = H - (y + h);
      gl.viewport(x, glY, w, h);
      gl.scissor(x, glY, w, h);
      gl.uniform4f(this.u.uRect, x, glY, w, h);
      if (t.kind === "raw") {
        const fit = this.rawFit(t.rect);
        gl.uniform1i(this.u.uMode, 1);
        gl.uniform3f(this.u.uRawFit, fit.ox, fit.oy, fit.k);
      } else {
        const v = settle(this.lens, this.views[t.idx], w / h);
        this.views[t.idx] = v;
        const b = basis(v);
        gl.uniform1i(this.u.uMode, 0);
        gl.uniform3f(this.u.uF, ...b.f);
        gl.uniform3f(this.u.uR, ...b.r);
        gl.uniform3f(this.u.uU, ...b.u);
        gl.uniform1f(this.u.uTanHalf, Math.tan((v.fov * DEG) / 2));
      }
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    this.drawOverlay(tiles);
    if (this.onRender) {
      const tile = tiles.find((t) => t.kind === "view" && t.idx === this.active);
      this.onRender(
        tile ? { view: { ...this.views[this.active] }, aspect: tile.rect.w / tile.rect.h } : null,
      );
    }
  }

  private drawOverlay(tiles: Tile[]) {
    const o = this.overlay;
    const dpr = window.devicePixelRatio || 1;
    for (const t of tiles) {
      const { x, y, w, h } = t.rect;
      if (t.kind === "raw") {
        const fit = this.rawFit(t.rect);
        const toRect = (p: { x: number; y: number }): [number, number] => [
          x + (p.x - fit.ox) / fit.k,
          y + (p.y - fit.oy) / fit.k,
        ];
        o.save();
        o.beginPath();
        o.rect(x, y, w, h);
        o.clip();
        const shown = this.layout === "quad" ? this.views.map((_, i) => i).slice(0, 4) : this.layout === "single" ? [this.active] : [];
        for (const i of shown) this.drawFootprint(i, toRect);
        o.restore();
        if (t.inset) {
          o.strokeStyle = "rgba(148,163,184,0.45)";
          o.lineWidth = dpr;
          o.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
        }
      } else if (this.layout === "quad") {
        const active = t.idx === this.active && this.interactive;
        o.strokeStyle = active ? VIEW_COLORS[t.idx % 4] : "rgba(15,23,42,1)";
        o.lineWidth = (active ? 3 : 2) * dpr;
        o.strokeRect(x + o.lineWidth / 2, y + o.lineWidth / 2, w - o.lineWidth, h - o.lineWidth);
      }
    }
  }

  private drawFootprint(i: number, toRect: (p: { x: number; y: number }) => [number, number]) {
    const o = this.overlay;
    const view = this.views[i];
    const b = basis(view);
    const aspect = this.aspectOf(i);
    const th = Math.tan((view.fov * DEG) / 2);
    const tmax = this.lens.theta_max_deg * DEG;
    const c = this.circle();
    const edge: Array<[number, number]> = [];
    const N = 24;
    for (let k = 0; k < N; k++) edge.push([-1 + (2 * k) / N, 1]);
    for (let k = 0; k < N; k++) edge.push([1, 1 - (2 * k) / N]);
    for (let k = 0; k < N; k++) edge.push([1 - (2 * k) / N, -1]);
    for (let k = 0; k < N; k++) edge.push([-1, -1 + (2 * k) / N]);
    o.strokeStyle = VIEW_COLORS[i % 4];
    o.lineWidth = (i === this.active ? 2.5 : 1.5) * (window.devicePixelRatio || 1);
    o.beginPath();
    let pen = false;
    for (const [nx, ny] of [...edge, edge[0]]) {
      const d = [0, 1, 2].map((k) => b.f[k] + nx * th * aspect * b.r[k] + ny * th * b.u[k]) as Vec3;
      const p = dirToPx(d, this.lens, c);
      if (p.theta > tmax) {
        pen = false;
        continue;
      }
      const [px, py] = toRect(p);
      if (pen) o.lineTo(px, py);
      else o.moveTo(px, py);
      pen = true;
    }
    o.stroke();
  }

  // ---------------------------------------------------------------- input

  private hit(px: number, py: number): Tile | null {
    const tiles = this.tiles();
    for (let i = tiles.length - 1; i >= 0; i--) {
      const r = tiles[i].rect;
      if (px >= r.x && px < r.x + r.w && py >= r.y && py < r.y + r.h) return tiles[i];
    }
    return null;
  }

  private aimFromRaw(t: Tile, px: number, py: number) {
    const fit = this.rawFit(t.rect);
    const tx = fit.ox + (px - t.rect.x) * fit.k;
    const ty = fit.oy + (py - t.rect.y) * fit.k;
    const c = this.circle();
    if (Math.hypot(tx - c.cx, ty - c.cy) > c.r) return;
    const idx = this.active;
    const aimed = aimAt(this.lens, this.views[idx], pxToDir(tx, ty, this.lens, c));
    this.views[idx] = settle(this.lens, aimed, this.aspectOf(idx));
    this.changed();
  }

  private changed() {
    this.render();
    this.onChange?.(this.views.map((v) => ({ ...v })), this.active);
  }

  private bindInput() {
    const el = this.overlayCanvas;
    const pos = (e: PointerEvent | WheelEvent) => {
      const r = el.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      return [(e.clientX - r.left) * dpr, (e.clientY - r.top) * dpr];
    };
    const down = (e: PointerEvent) => {
      if (!this.interactive || !this.hasFrame || e.button !== 0) return;
      const [px, py] = pos(e);
      const t = this.hit(px, py);
      if (!t) return;
      el.setPointerCapture(e.pointerId);
      if (t.kind === "view" && t.idx !== this.active) {
        this.active = t.idx;
        this.changed();
      }
      // Aiming from the fisheye is a gesture even without movement.
      this.drag = { tile: t, x: px, y: py, moved: t.kind === "raw" };
      if (t.kind === "raw") this.aimFromRaw(t, px, py);
    };
    const move = (e: PointerEvent) => {
      const d = this.drag;
      if (!d) return;
      const [px, py] = pos(e);
      if (Math.hypot(px - d.x, py - d.y) > 3) d.moved = true;
      if (d.tile.kind === "raw") {
        this.aimFromRaw(d.tile, px, py);
      } else {
        const idx = d.tile.idx;
        const v = this.views[idx];
        const degPerPx = v.fov / d.tile.rect.h;
        const aspect = d.tile.rect.w / d.tile.rect.h;
        // Pan and tilt are limited independently, so a diagonal drag that
        // runs into the tilt limit keeps panning along it.
        const panned = moveWithinLens(this.lens, v, { ...v, yaw: wrapYaw(v.yaw + (px - d.x) * degPerPx) }, aspect);
        const tilted = moveWithinLens(
          this.lens,
          panned,
          { ...panned, tilt: clampTilt(this.lens, panned.tilt - (py - d.y) * degPerPx) },
          aspect,
        );
        this.views[idx] = tilted;
        this.changed();
      }
      d.x = px;
      d.y = py;
    };
    const up = () => {
      if (this.drag?.moved) this.suppressClick = true;
      this.drag = null;
    };
    // A drag is not a click: containers that toggle on click (enlarge /
    // return to grid) must not fire at the end of a look-around.
    const click = (e: MouseEvent) => {
      if (this.suppressClick) {
        this.suppressClick = false;
        e.stopPropagation();
        e.preventDefault();
      }
    };
    const wheel = (e: WheelEvent) => {
      if (!this.interactive || !this.hasFrame) return;
      e.preventDefault();
      const [px, py] = pos(e);
      const t = this.hit(px, py);
      const idx = t && t.kind === "view" ? t.idx : this.active;
      const v = this.views[idx];
      this.views[idx] = moveWithinLens(
        this.lens,
        v,
        { ...v, fov: clampFov(v.fov * Math.exp(e.deltaY * 0.0012)) },
        this.aspectOf(idx),
      );
      this.changed();
    };
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    el.addEventListener("click", click, true);
    el.addEventListener("wheel", wheel, { passive: false });
    this.cleanup.push(() => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      el.removeEventListener("click", click, true);
      el.removeEventListener("wheel", wheel);
    });
  }

  private link(): WebGLProgram {
    const gl = this.gl;
    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type);
      if (!sh) throw new Error("shader allocation failed");
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) || "shader");
      return sh;
    };
    const prog = gl.createProgram();
    if (!prog) throw new Error("program allocation failed");
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) || "link");
    return prog;
  }
}
